import DatabaseModel from './DatabaseModel.js';
import { unwrapDto } from './responseUtils.js';

var RhCoincidencias = Object.create(DatabaseModel);

RhCoincidencias.model = 'rh/reglas-coincidencia';
RhCoincidencias.modelFront = 'reglascoincidencia';

var MODEL_MOTOR = 'rh/coincidencias';

// GET /movilidad/rh/reglas-coincidencia[?estado=ACEPTADA]
// → lista de { id, titulo, tipoMiembros, funciones[], funcionesInternas[], personas[], materia,
//              minimoPresentes, severidad, estado, origen, explicacion, miembros[],
//              numeroMiembros, avisos[], historico[] }
// `funciones` son los códigos que se VEN en Ajustes · Funciones y `funcionesInternas` los mismos
// en el mismo orden pero por su código interno, que no cambia nunca (#483): es la identidad que
// la pantalla devuelve al guardar para lo que no se haya tocado.
// Las reglas viajan en `list` del ResponseDto, así que unwrapDto devuelve null si no hay
// ninguna: de ahí el `|| []`.
RhCoincidencias.listar = function(estado) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + (estado ? '?estado=' + estado : ''),
        method: 'GET'
    }, false).then(function(res) {
        return unwrapDto(res) || [];
    });
};

// POST /movilidad/rh/reglas-coincidencia → la regla creada, ya con sus miembros resueltos.
// Nace ACEPTADA y con origen MANUAL: no hay que aceptarla después.
RhCoincidencias.crear = function(regla) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model,
        method: 'POST',
        data: regla
    }, false).then(unwrapDto);
};

// PUT /movilidad/rh/reglas-coincidencia/{id} → la regla editada.
RhCoincidencias.editar = function(id, regla) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/' + encodeURIComponent(id),
        method: 'PUT',
        data: regla
    }, false).then(unwrapDto);
};

// POST /movilidad/rh/reglas-coincidencia/{id}/estado → aceptar, descartar o reactivar.
// Descartar y reactivar exigen motivo; el backend lo rechaza si falta.
RhCoincidencias.cambiarEstado = function(id, estado, motivo) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/' + encodeURIComponent(id) + '/estado',
        method: 'POST',
        data: { estado: estado, motivo: motivo }
    }, false).then(unwrapDto);
};

// GET /movilidad/rh/reglas-coincidencia/propuestas-ia/previo
// → { propuestasSinRevisar, aviso, iaHabilitada }
// Se llama ANTES de pedir propuestas: las que están por revisar no se sustituyen, se quedan,
// y hay que avisar de cuántas hay para no acabar con la pantalla llena de repetidas.
RhCoincidencias.previoPropuestasIa = function() {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/propuestas-ia/previo',
        method: 'GET'
    }, false).then(unwrapDto);
};

// POST /movilidad/rh/reglas-coincidencia/propuestas-ia
// → { propuestas[], descartadas[], modelo, auditId, avisos[], mensaje, propuestasSinRevisarAntes }
//
// Nada de lo que devuelve está aplicado: las propuestas nacen en estado PROPUESTA y no evalúan
// nada hasta que alguien las acepta. Si el servicio de IA no responde, esto NO falla: contesta
// con `avisos` y sin propuestas nuevas.
RhCoincidencias.pedirPropuestasIa = function() {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/propuestas-ia',
        method: 'POST',
        data: {}
    }, false).then(unwrapDto);
};

// POST /movilidad/rh/reglas-coincidencia/{id}/sustituir → la regla NUEVA, manual y aceptada.
// Es lo que hace «Editar» sobre una propuesta de la IA: no se edita la propuesta —lo que
// propuso queda intacto para el rastro—, se crea una regla con lo editado y la propuesta pasa
// a descartada, marcada como sustituida por ella.
RhCoincidencias.sustituirPropuesta = function(id, regla) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/' + encodeURIComponent(id) + '/sustituir',
        method: 'POST',
        data: regla
    }, false).then(unwrapDto);
};

// POST /movilidad/rh/coincidencias/evaluar
// → { semaforo, diasLaborables, reglas[], reglasEvaluadas, conNombres, requiereJustificacion,
//     alternativas[], mensajeAlternativas }
//
// `conNombres` lo decide el servidor: si pregunta el propio solicitante, los nombres de los
// compañeros NO viajan en la respuesta (solo los contadores). No intentes pintarlos: no están.
RhCoincidencias.evaluar = function(datos) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + MODEL_MOTOR + '/evaluar',
        method: 'POST',
        data: datos
    }, false).then(unwrapDto);
};

// POST /movilidad/rh/coincidencias/validar-aprobacion
// → { semaforo, requiereJustificacion, justificacionRegistrada, evaluacion }
// Da error si el semáforo sale rojo y no se manda justificación.
//
// `datos.solicitudId` es OBLIGATORIO: se comprueba la solicitud guardada, y el solicitante
// sale de ella, no de lo que se mande aquí. Para pintar el semáforo de un tramo que todavía no
// existe está `evaluar()`.
//
// `evaluacion` sigue la misma regla de nombres que `evaluar`: cuando quien comprueba es el
// propio solicitante —el ADMIN único que se valida a sí mismo (D18)— llega con
// `conNombres: false` y sin los compañeros, solo con los contadores. La justificación se le
// exige igual.
RhCoincidencias.validarAprobacion = function(datos) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + MODEL_MOTOR + '/validar-aprobacion',
        method: 'POST',
        data: datos
    }, false).then(unwrapDto);
};

// POST /movilidad/rh/coincidencias/explicar
// → { semaforo, explicacion, borradorMotivo, accion, fuente, modelo, avisos[], evaluacion }
//
// SOLO para quien puede ver nombres: el aprobador de esa persona, su suplente o ADMIN/DIRECTOR.
// Al solicitante no se le pide nunca: su explicación es de plantilla y ya viene dentro de
// `evaluar`, sin nombres y sin pasar por la IA.
//
// `fuente` dice quién escribió el texto: IA, o PLANTILLA cuando el servicio de IA no ha
// respondido. El color es siempre el del motor, responda la IA lo que responda.
RhCoincidencias.explicar = function(datos) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + MODEL_MOTOR + '/explicar',
        method: 'POST',
        data: datos
    }, false).then(unwrapDto);
};

export default RhCoincidencias;
